import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import {
  GetSupplierDetailsDataDocument,
  type GetSupplierDetailsDataQuery,
  type GetSupplierDetailsDataQueryVariables,
} from '../../../generated';

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Client-side validation for supplier image uploads.
 * Returns a human-readable error message, or null when the file is valid.
 */
export const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Only JPEG, PNG, or WebP images can be uploaded.';
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return 'The image is too large — the maximum size is 10 MB.';
  }
  return null;
};

export interface SupplierUploadUrls {
  coverPhotoUploadUrl: string | null;
  logoUploadUrl: string | null;
}

interface UseSupplierImageUploadParams {
  supplierId: string;
}

interface UseSupplierImageUploadResult {
  /**
   * PUT the image to the presigned uploadUrl. Resolves on success, rejects
   * with an Error on failure. Only ever call this from an explicit user
   * action — persistence is automatic server-side once the PUT succeeds.
   */
  upload: (file: File | Blob, uploadUrl: string) => Promise<void>;
  isUploading: boolean;
  /** Upload progress 0–100 while an upload is in flight. */
  progress: number;
  error: string | null;
  /**
   * Fetch fresh presigned upload URLs. Presigned URLs expire after ~15
   * minutes, so call this right before uploading when the form may have been
   * open for a while.
   */
  refreshUploadUrls: () => Promise<SupplierUploadUrls>;
  /**
   * Network-only refetch of the supplier detail query — updates the Apollo
   * cache so components pick up server-generated crops after an upload.
   */
  refetchSupplierDetails: () => Promise<void>;
}

/**
 * Upload a supplier cover photo or logo (bot-user profile image) to its
 * presigned S3 URL.
 *
 * Backend contract (mirrors legacy admin usePhotoChooser):
 * - Direct PUT of the raw image blob to `uploadUrl` with an EMPTY
 *   Content-Type header (S3 infers the type).
 * - There is NO save mutation: an S3 event → SQS → userImageUploaded handler
 *   persists the image, and the image service produces the size crops
 *   asynchronously shortly after.
 */
export const useSupplierImageUpload = ({
  supplierId,
}: UseSupplierImageUploadParams): UseSupplierImageUploadResult => {
  const client = useApolloClient();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File | Blob, uploadUrl: string): Promise<void> => {
      setIsUploading(true);
      setProgress(0);
      setError(null);

      try {
        await new Promise<void>((resolve, reject) => {
          // XMLHttpRequest instead of fetch so we can report upload progress.
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', uploadUrl);
          // Empty Content-Type is required by the presigned URL — S3 infers it.
          xhr.setRequestHeader('Content-Type', '');
          xhr.upload.onprogress = (event: ProgressEvent) => {
            if (event.lengthComputable) {
              setProgress(Math.round((event.loaded / event.total) * 100));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(
                new Error(
                  `Image upload failed (status ${xhr.status}). The upload link may have expired — please try again.`
                )
              );
            }
          };
          xhr.onerror = () =>
            reject(new Error('Image upload failed due to a network error.'));
          xhr.send(file);
        });
        setProgress(100);
      } catch (uploadError) {
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : 'Image upload failed.';
        setError(message);
        throw uploadError instanceof Error ? uploadError : new Error(message);
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  const refetchSupplierDetails = useCallback(async (): Promise<void> => {
    await client.query<
      GetSupplierDetailsDataQuery,
      GetSupplierDetailsDataQueryVariables
    >({
      query: GetSupplierDetailsDataDocument,
      variables: { id: supplierId },
      fetchPolicy: 'network-only',
    });
  }, [client, supplierId]);

  const refreshUploadUrls =
    useCallback(async (): Promise<SupplierUploadUrls> => {
      const { data } = await client.query<
        GetSupplierDetailsDataQuery,
        GetSupplierDetailsDataQueryVariables
      >({
        query: GetSupplierDetailsDataDocument,
        variables: { id: supplierId },
        fetchPolicy: 'network-only',
      });

      return {
        coverPhotoUploadUrl: data.supplier?.coverPhoto?.uploadUrl ?? null,
        logoUploadUrl: data.supplier?.botUser?.profileImage?.uploadUrl ?? null,
      };
    }, [client, supplierId]);

  return {
    upload,
    isUploading,
    progress,
    error,
    refreshUploadUrls,
    refetchSupplierDetails,
  };
};
